import React, { useEffect, useRef, useState } from "react";
import { NewButton, NewInput } from "../..";
import { ButtonTypes, NewFieldStyles, NewFieldTypes, ValueSteps } from "../../../Definitions";
import { EditAbleCell, StyledTableCellProps } from "./styledTableCell";
import { IdentifiableRow } from "../reactiveTableRenderer";
import { CellContext, Row } from "@tanstack/react-table";
import { focus_view } from "../../../Assets/Glyphs/focusView";
import { colors, glyphs } from "../../../Styles";

export interface StepOptions<T> {
  valueStep: ValueSteps,
  maxValueNumber: number | undefined | ((rowId: number) => number);
  minValueNumber?: (rowId: number) => number;
}

export const MutableTableCell = <T extends IdentifiableRow,>({ getValue, row, table, column, canEdit, staticColumn, cell, valueStep, maxValueNumber, minValueNumber }: CellContext<T, number | undefined> & EditAbleCell<T> & StepOptions<T>) => {
  const initialValue = getValue();

  const [value, setValue] = useState(initialValue);

  const onBlur = (valueOverwrite?: number | string) => {
    if (value !== initialValue || (valueOverwrite !== undefined && valueOverwrite !== value)) {
      table.options.meta?.updateData(row.original.id, column.id, valueOverwrite !== undefined ? valueOverwrite : value, row);
    }
  }
  let maxValue;
  if (maxValueNumber !== undefined && typeof maxValueNumber === "number") {
    maxValue = maxValueNumber;
  }
  if (maxValueNumber !== undefined && typeof maxValueNumber === "function") {
    maxValue = maxValueNumber(row.original.id);
  }
  let minValue = 0;
  if (minValueNumber !== undefined) {
    minValue = minValueNumber(row.original.id);
  }
  const isStatic = typeof staticColumn === "function" ? staticColumn(row) : staticColumn;
  return <StyledNumberCell staticColumn={isStatic} key={cell.id} keyPass={cell.id + "Input"} onBlur={onBlur} onChange={(e: number) => setValue(e)} canEdit={canEdit} cellData={{ value }} valueStep={valueStep} maxValueNumber={maxValue} minValueNumber={minValue} />
}

const StyledNumberCell = <T extends IdentifiableRow>({ onBlur, onChange, onDelete, canEdit, cellData, keyPass, staticColumn, valueStep, maxValueNumber, minValueNumber, focus_view }: StyledTableCellProps & EditAbleCell<T> & { valueStep: ValueSteps, maxValueNumber: number | undefined, minValueNumber: number | undefined }) => {
  const [hover, setHover] = useState<boolean>(false)
  const [empty, setEmpty] = useState<boolean>(cellData.value === undefined || cellData.value === "")
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!empty) {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }
  }, [empty]);

  if (canEdit) {
    if (staticColumn) {
      return (
        <div style={{ ...cellData.style, opacity: 0.5 }} key={keyPass}>
          {String(cellData.value) ?? ''}
        </div>
      );
    } else if (empty) {
      return (
        <div style={{ display: "flex", alignItems: "center" }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
          <div style={{ opacity: hover ? "0.5" : "0", transition: "opacity 0.3s ease", }} onClick={() => setEmpty(false)}>
            <NewInput
              key={keyPass}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary,
                fieldType: NewFieldTypes.tertiaryField,
                fieldWidth: "100%",
                focus_view: focus_view
              }}
              valueOverrides={{
                valueType: "number",
                valueStep: valueStep,
                minValueNumber,
                maxValueNumber
              }}
            />
          </div>
          <div style={{ width: "20px" }} />
        </div>
      );
    }
    else {
      return (
        <div style={{ display: "flex", alignItems: "center" }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
          <NewInput
            onBlur={() => { if (onBlur) { onBlur() }; if (cellData.value === "") { setEmpty(true) } }}
            inputRef={inputRef}
            key={keyPass}
            onChange={onChange}
            isRequired
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary,
              fieldType: NewFieldTypes.tertiaryField,
              fieldWidth: "100%",
              focus_view: focus_view
            }}
            valueOverrides={{
              defaultValue: cellData.value,
              valueType: "number",
              valueStep: valueStep,
              minValueNumber,
              maxValueNumber
            }}
          />
          <NewButton
            content=""
            buttonType={ButtonTypes.glyphButton}
            startEnhancer={glyphs.Close({ fillColor: colors.greyDarkPrimary })}
            onClick={() => { setEmpty(!empty); if (onBlur && onChange) { onChange(""); onBlur("") } }}
            overrides={{
              startEnhancerStyles: { opacity: hover ? "1" : "0", transition: "opacity 0.3s ease", }
            }} />
        </div>
      )
    }
  }

  return (cellData.value)
}
